import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MapPin, Mail, Clock, ArrowUpRight, Flame } from "lucide-react";
import MaskedHeading from "@/components/MaskedHeading";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import SocialIcons, { WhatsAppIcon } from "@/components/SocialIcons";
import { SectionHeader, PhoneCTA, BackHomeButton, FloatingBar, MapEmbed, Stars, scrollToId } from "@/components/shared";
import { pizzeria } from "@/data/demoData";

function Header() {
    return (
        <header className="sticky top-0 z-40 border-b border-swiss-line bg-white/90 backdrop-blur-xl">
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-10">
                <span className="text-xl font-bold tracking-tight text-ink">Pizzeria Demo</span>
                <nav className="hidden items-center gap-8 md:flex">
                    {[
                        ["Menù", "menu"],
                        ["Gallery", "gallery"],
                        ["Recensioni", "recensioni"],
                        ["Contatti", "contatti"],
                    ].map(([label, id]) => (
                        <button
                            key={id}
                            data-testid={`nav-${id}`}
                            onClick={() => scrollToId(id)}
                            className="text-xs font-bold uppercase tracking-[0.2em] text-swiss-gray transition-colors hover:text-swiss-red"
                        >
                            {label}
                        </button>
                    ))}
                </nav>
                <a
                    href={`tel:${pizzeria.contacts.phoneRaw}`}
                    data-testid="header-call-btn"
                    className="inline-flex items-center gap-2 bg-swiss-red px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:bg-ink"
                >
                    <Phone className="h-3.5 w-3.5" />
                    Chiama
                </a>
            </div>
        </header>
    );
}

function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

    return (
        <section ref={ref} className="relative overflow-hidden">
            <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 pb-16 pt-14 sm:px-10 lg:grid-cols-2 lg:gap-16 lg:pb-24 lg:pt-24">
                <div>
                    <Reveal y={16}>
                        <p className="mb-6 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.4em] text-swiss-red sm:text-xs">
                            <span className="inline-block h-px w-10 bg-swiss-red" />
                            {pizzeria.eyebrow}
                        </p>
                    </Reveal>
                    <MaskedHeading
                        lines={["Pizzeria", "Demo"]}
                        delay={0.15}
                        className="text-6xl font-bold leading-[0.98] tracking-tight text-ink sm:text-8xl lg:text-[7rem]"
                    />
                    <Reveal delay={0.5}>
                        <p className="mt-6 text-xl font-medium text-swiss-gray sm:text-2xl">{pizzeria.slogan}</p>
                        <p className="mt-5 max-w-md text-sm leading-relaxed text-swiss-gray sm:text-base">{pizzeria.description}</p>
                    </Reveal>
                    <Reveal delay={0.65}>
                        <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
                            <PhoneCTA
                                phoneRaw={pizzeria.contacts.phoneRaw}
                                phoneDisplay={pizzeria.contacts.phone}
                                label="Chiama per Prenotare un Tavolo"
                                testid="call-to-book-table-btn"
                                className="bg-swiss-red text-white hover:bg-ink"
                            />
                            <button
                                data-testid="hero-view-menu-btn"
                                onClick={() => scrollToId("menu")}
                                className="inline-flex items-center justify-center gap-2 border-2 border-ink px-7 py-4 text-xs font-bold uppercase tracking-widest text-ink transition-colors duration-300 hover:bg-ink hover:text-white sm:text-sm"
                            >
                                Scopri il Menù
                                <ArrowUpRight className="h-4 w-4" />
                            </button>
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={0.35} className="relative">
                    <div className="relative h-[380px] overflow-hidden sm:h-[520px]">
                        <motion.img
                            src={pizzeria.heroImage}
                            alt="La nostra pizza al forno"
                            style={{ y }}
                            className="h-full w-full scale-[1.18] object-cover"
                        />
                    </div>
                    <div className="absolute bottom-0 left-0 flex items-center gap-2 bg-ink px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-white">
                        <Flame className="h-3.5 w-3.5 text-swiss-red" />
                        Lugano · Via Nassa
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

function Menu() {
    return (
        <section id="menu" className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-28">
            <SectionHeader label="Il Menù" title="Dalla nostra cucina" />
            <div className="mt-12 grid gap-6 md:grid-cols-2">
                {pizzeria.menu.map((section, i) => (
                    <Reveal key={i} delay={(i % 2) * 0.08}>
                        <div
                            data-testid={`menu-section-${section.name.toLowerCase()}`}
                            className="h-full border border-swiss-line bg-white p-7 sm:p-9"
                        >
                            <div className="mb-6 flex items-center gap-4">
                                <span className="inline-block h-3 w-3 bg-swiss-red" />
                                <h3 className="text-2xl font-bold uppercase tracking-tight text-ink sm:text-3xl">{section.name}</h3>
                                <span className="h-px flex-1 bg-ink/20" />
                            </div>
                            <div className="space-y-5">
                                {section.items.map((item, j) => (
                                    <div key={j} className="flex items-baseline" data-testid={`menu-item-${section.name.toLowerCase()}-${j}`}>
                                        <div className="max-w-[70%]">
                                            <p className="text-base font-bold text-ink sm:text-lg">{item.name}</p>
                                            <p className="mt-1 text-xs leading-relaxed text-swiss-gray">{item.desc}</p>
                                        </div>
                                        <span className="leader" />
                                        <p className="shrink-0 text-lg font-bold text-swiss-red">{item.price}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

function Gallery() {
    return (
        <section id="gallery" className="border-y border-swiss-line bg-swiss-off py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-6 sm:px-10">
                <SectionHeader label="Fotogallery" title="Atmosfera & Sapori" />
                <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                    {pizzeria.gallery.map((g, i) => (
                        <Reveal key={i} delay={(i % 4) * 0.08} className={i % 2 === 0 ? "pb-0" : "lg:pt-10"}>
                            <figure
                                data-testid={`gallery-item-${i}`}
                                className="group relative aspect-[3/4] overflow-hidden border border-swiss-line bg-white"
                            >
                                <img
                                    src={g.src}
                                    alt={g.title}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/90 via-transparent to-transparent opacity-75 transition-opacity duration-500 group-hover:opacity-95" />
                                <figcaption className="absolute bottom-0 left-0 right-0 p-4">
                                    <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-swiss-red">{g.tag}</p>
                                    <p className="mt-1 text-base font-bold leading-tight text-white">{g.title}</p>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function CallBand() {
    return (
        <section className="bg-swiss-red py-20 text-center lg:py-24">
            <div className="mx-auto max-w-3xl px-6">
                <Reveal>
                    <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.4em] text-white/80">Prenota il tuo tavolo</p>
                    <h2 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-6xl">
                        La cena perfetta comincia con una telefonata
                    </h2>
                    <p className="mt-5 text-sm text-white/85 sm:text-base">
                        Sera di punta o pranzo di famiglia: riservaci un tavolo in un attimo.
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="mt-10">
                        <PhoneCTA
                            phoneRaw={pizzeria.contacts.phoneRaw}
                            phoneDisplay={pizzeria.contacts.phone}
                            label="Chiama per Prenotare un Tavolo"
                            testid="call-to-book-table-btn-band"
                            className="bg-white text-swiss-red hover:text-ink"
                        />
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

function Contacts() {
    const c = pizzeria.contacts;
    return (
        <section id="contatti" className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-28">
            <SectionHeader label="Contatti" title="Vieni a trovarci" />
            <div className="mt-12 grid gap-8 lg:grid-cols-2">
                <Reveal>
                    <div className="flex h-full flex-col justify-between gap-8 border border-swiss-line p-7 sm:p-9">
                        <div className="space-y-5">
                            <div className="flex items-start gap-4" data-testid="contacts-block">
                                <MapPin className="mt-1 h-5 w-5 shrink-0 text-swiss-red" />
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-swiss-gray">Indirizzo</p>
                                    <p className="mt-1 text-sm text-ink sm:text-base">{c.address}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Phone className="mt-1 h-5 w-5 shrink-0 text-swiss-red" />
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-swiss-gray">Telefono</p>
                                    <a href={`tel:${c.phoneRaw}`} data-testid="contacts-phone-link" className="mt-1 block text-sm text-ink transition-colors hover:text-swiss-red sm:text-base">
                                        {c.phone}
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <WhatsAppIcon className="mt-1 h-5 w-5 shrink-0 text-swiss-red" />
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-swiss-gray">WhatsApp</p>
                                    <a href={`https://wa.me/${c.whatsappRaw.replace("+", "")}`} target="_blank" rel="noopener noreferrer" data-testid="contacts-whatsapp-link" className="mt-1 block text-sm text-ink transition-colors hover:text-swiss-red sm:text-base">
                                        {c.whatsapp}
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Mail className="mt-1 h-5 w-5 shrink-0 text-swiss-red" />
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-swiss-gray">Email</p>
                                    <a href={`mailto:${c.email}`} data-testid="contacts-email-link" className="mt-1 block text-sm text-ink transition-colors hover:text-swiss-red sm:text-base">
                                        {c.email}
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="border-t border-swiss-line pt-6">
                            <div className="mb-4 flex items-center gap-4" data-testid="referente-card">
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-ink text-lg font-bold text-white">
                                    GE
                                </span>
                                <div>
                                    <p className="text-xl font-bold text-ink">{pizzeria.titolare.name}</p>
                                    <p className="text-xs uppercase tracking-[0.2em] text-swiss-red">{pizzeria.titolare.role}</p>
                                </div>
                            </div>
                            <div className="space-y-2.5">
                                <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-swiss-gray">
                                    <Clock className="h-4 w-4 text-swiss-red" />
                                    Orari di apertura
                                </div>
                                {pizzeria.orari.map((o, i) => (
                                    <div key={i} className="flex items-baseline text-sm" data-testid={`orari-row-${i}`}>
                                        <span className="text-ink">{o.days}</span>
                                        <span className="leader" />
                                        <span className={o.hours === "Chiuso" ? "text-swiss-gray italic" : "font-bold text-ink"}>{o.hours}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Reveal>
                <Reveal delay={0.12}>
                    <MapEmbed query={c.mapQuery} testid="map-embed-pizzeria" />
                </Reveal>
            </div>
        </section>
    );
}

function Reviews() {
    return (
        <section id="recensioni" className="border-t border-swiss-line bg-swiss-off py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-6 sm:px-10">
                <SectionHeader label="Parlano di noi" title="Dicono dei nostri clienti" align="center" />
                <div className="mt-14 grid gap-6 md:grid-cols-3">
                    {pizzeria.reviews.map((r, i) => (
                        <Reveal key={i} delay={i * 0.1}>
                            <figure
                                data-testid={`review-card-${i}`}
                                className="flex h-full flex-col border border-swiss-line bg-white p-7 transition-colors duration-300 hover:border-ink"
                            >
                                <Stars rating={r.rating} />
                                <blockquote className="mt-5 flex-1 text-base leading-relaxed text-ink/85">“{r.quote}”</blockquote>
                                <figcaption className="mt-6 border-t border-swiss-line pt-4">
                                    <p className="text-sm font-bold text-ink">{r.author}</p>
                                    <p className="mt-0.5 text-xs uppercase tracking-[0.2em] text-swiss-red">{r.service}</p>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2} className="mt-16">
                    <p className="mb-7 text-center text-xs font-bold uppercase tracking-[0.3em] text-swiss-gray">Seguici sui social</p>
                    <SocialIcons socials={pizzeria.socials} testPrefix="social" />
                </Reveal>
            </div>
        </section>
    );
}

export default function PizzeriaPage() {
    return (
        <div data-testid="pizzeria-page" className="min-h-screen bg-white pb-28 text-ink lg:pb-0">
            <Header />
            <Hero />
            <div className="text-ink">
                <Marquee items={pizzeria.marquee} className="border-swiss-line" />
            </div>
            <Menu />
            <Gallery />
            <CallBand />
            <Contacts />
            <Reviews />
            <BackHomeButton />
            <FloatingBar phoneRaw={pizzeria.contacts.phoneRaw} callLabel="Prenota" />
        </div>
    );
}
